import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Select,
  Segmented,
  Spin,
  Alert,
  Tag,
  Descriptions,
  Empty,
} from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { API } from '@/services/api.service';

/**
 * Shared, reusable annual-leave (GO) history view for a single employee.
 * Renders the current balance, a line chart of total/past/current over time,
 * and a chronological events table (realized holiday-leave requests merged with
 * raw balance adjustments). Used both in the tech-admin tool and on the
 * employee profile.
 */
export default function HolidayHistoryView({ employeeId }) {
  const { t } = useTranslation(['translations']);
  const th = (key, opts) => t(`holidayHistory.${key}`, { ns: 'translations', ...opts });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null); // null => all years
  // 'recorded' = stored snapshots, 'computed' = reconstructed ledger, 'compare' = both.
  const [mode, setMode] = useState('recorded');

  useEffect(() => {
    if (!employeeId) {
      setData(null);
      return undefined;
    }
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await API.employees.getEmployeeHolidayHistory(
          employeeId,
          year || undefined
        );
        if (active) setData(res);
      } catch (err) {
        if (active) setError(err?.response?.data?.message || err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [employeeId, year]);

  // Format any date (ISO timestamp or "YYYY-MM-DD" string) as DD.MM.YYYY.
  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '-';
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${dt.getFullYear()}.`;
  };

  const eventLabel = (e) => {
    if (e.kind === 'request') return th('labelLeave');
    const map = {
      expiry: 'labelExpiry',
      rollover: 'labelRollover',
      manual: 'labelManual',
    };
    return th(map[e.labelCode] || 'labelManual');
  };

  // Table shows newest first; chart keeps ascending (the API order).
  const tableData = useMemo(
    () => (data?.events ? [...data.events].reverse() : []),
    [data]
  );

  const baseColumns = [
    {
      title: th('colDate'),
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (d) => formatDate(d),
    },
    {
      title: th('colEvent'),
      key: 'event',
      width: 170,
      render: (_, e) => (
        <Tag color={e.kind === 'request' ? 'blue' : 'gold'}>{eventLabel(e)}</Tag>
      ),
    },
    {
      title: th('colPeriod'),
      key: 'period',
      width: 170,
      render: (_, e) =>
        e.kind === 'request' && e.dateFrom
          ? `${formatDate(e.dateFrom)}${
              e.dateTo && e.dateTo !== e.dateFrom ? ` – ${formatDate(e.dateTo)}` : ''
            }`
          : '-',
    },
    {
      title: th('colDays'),
      dataIndex: 'daysConsumed',
      key: 'days',
      width: 70,
      render: (d) => (d != null ? d : '-'),
    },
    {
      title: th('colStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s) => (s ? th(`status${s}`) : '-'),
    },
  ];

  const recordedBalanceColumns = [
    {
      title: th('colTotalAfter'),
      key: 'total',
      width: 100,
      render: (_, e) => (e.balance?.total != null ? <strong>{e.balance.total}</strong> : '-'),
    },
    {
      title: th('colPastAfter'),
      key: 'past',
      width: 90,
      render: (_, e) => (e.balance?.past != null ? e.balance.past : '-'),
    },
    {
      title: th('colCurrentAfter'),
      key: 'current',
      width: 100,
      render: (_, e) => (e.balance?.current != null ? e.balance.current : '-'),
    },
  ];

  const computedBalanceColumns = [
    {
      title: th('colTotalComputed'),
      key: 'recon',
      width: 130,
      render: (_, e) =>
        e.reconstructedTotal != null ? <strong>{e.reconstructedTotal}</strong> : '-',
    },
  ];

  const compareBalanceColumns = [
    {
      title: th('colTotalAfter'),
      key: 'total',
      width: 120,
      render: (_, e) => (e.balance?.total != null ? e.balance.total : '-'),
    },
    {
      title: th('colTotalComputed'),
      key: 'recon',
      width: 130,
      render: (_, e) => (e.reconstructedTotal != null ? e.reconstructedTotal : '-'),
    },
    {
      title: th('colDiff'),
      key: 'diff',
      width: 90,
      render: (_, e) => {
        const d =
          e.balance?.total != null && e.reconstructedTotal != null
            ? e.balance.total - e.reconstructedTotal
            : null;
        if (d == null) return '-';
        return (
          <span style={{ color: d === 0 ? undefined : '#cf1322' }}>
            {d > 0 ? `+${d}` : d}
          </span>
        );
      },
    },
  ];

  const balanceColumns =
    mode === 'computed'
      ? computedBalanceColumns
      : mode === 'compare'
        ? compareBalanceColumns
        : recordedBalanceColumns;

  const columns = [
    ...baseColumns,
    ...balanceColumns,
    {
      title: th('colUser'),
      dataIndex: 'user',
      key: 'user',
      width: 150,
      ellipsis: true,
      render: (u) => u || '-',
    },
  ];

  if (!employeeId) {
    return <Empty description={th('pickEmployee')} />;
  }

  const cs = data?.currentState || {};
  const yearOptions = [
    { value: '', label: th('allYears') },
    ...(data?.years || []).map((y) => ({ value: y, label: y })),
  ];

  return (
    <Spin spinning={loading}>
      <div className="space-y-4">
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-medium">{th('year')}:</span>
          <Select
            value={year || ''}
            onChange={(v) => setYear(v || null)}
            options={yearOptions}
            style={{ width: 160 }}
          />
          <span className="font-medium">{th('viewMode')}:</span>
          <Segmented
            value={mode}
            onChange={setMode}
            options={[
              { value: 'recorded', label: th('modeRecorded') },
              { value: 'computed', label: th('modeComputed') },
              { value: 'compare', label: th('modeCompare') },
            ]}
          />
        </div>

        {data && (
          <>
            <Card size="small" title={th('currentBalanceTitle')} className="shadow-sm">
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3, lg: 5 }}>
                <Descriptions.Item label={th('balTotal')}>
                  <strong>{cs.numberOfHolidayDays ?? '-'}</strong>
                </Descriptions.Item>
                <Descriptions.Item label={th('balPast')}>
                  {cs.numberOfHolidayDaysPast ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={th('balCurrent')}>
                  {cs.numberOfHolidayDaysCurrent ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={th('balUsed')}>
                  {cs.numberOfHolidayDaysUsed ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label={th('balByEnd')}>
                  {cs.numberOfHolidayDaysByEndOfContract ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {data.chart?.length > 0 && (
              <Card size="small" title={th('chartTitle')} className="shadow-sm">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.chart} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" height={40} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    {mode === 'recorded' && (
                      <Line
                        type="monotone"
                        dataKey="total"
                        name={th('seriesTotal')}
                        stroke="#1677ff"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    )}
                    {mode === 'recorded' && (
                      <Line
                        type="monotone"
                        dataKey="past"
                        name={th('seriesPast')}
                        stroke="#fa8c16"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    )}
                    {mode === 'recorded' && (
                      <Line
                        type="monotone"
                        dataKey="current"
                        name={th('seriesCurrent')}
                        stroke="#52c41a"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    )}
                    {mode === 'compare' && (
                      <Line
                        type="monotone"
                        dataKey="total"
                        name={th('seriesTotalRecorded')}
                        stroke="#1677ff"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    )}
                    {(mode === 'computed' || mode === 'compare') && (
                      <Line
                        type="monotone"
                        dataKey="reconstructedTotal"
                        name={th('seriesTotalComputed')}
                        stroke="#722ed1"
                        strokeWidth={2}
                        strokeDasharray={mode === 'compare' ? '5 4' : undefined}
                        dot={{ r: 3 }}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            )}

            <Card size="small" title={th('eventsTitle')} className="shadow-sm">
              {tableData.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={tableData}
                  rowKey="id"
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  scroll={{ x: 1070 }}
                  size="small"
                />
              ) : (
                <Empty description={th('noData')} />
              )}
            </Card>
          </>
        )}
      </div>
    </Spin>
  );
}
